<script setup lang="ts" generic="T">
  import { useElementSize } from '@vueuse/core';
  import { computed, ref, useSlots } from 'vue';
  import {
    LABEL_SIZE,
    SELECT_VARIANT_BY_SIZE,
    CLEAR_BUTTON_SIZE,
    CHEVRON_ICON_SIZE,
    MENU_SIZE,
    INFO_VARIANT_BY_SIZE,
    SELECT_VARIANT_BY_SIZE_READONLY,
  } from './lib';
  import { Field } from '../Field';
  import { SelectSize } from './Select.types';
  import { Typo, IconSize, DropdownMenu, Button, Icon, Input, TypoVariant, BottomSheet, Radio } from '@/components';

  type ModelValueType = T | T[keyof T] | keyof T | null;

  interface SelectProps {
    options: T[];
    modelValue: ModelValueType;
    label?: string;
    placeholder?: string;
    note?: string;
    filterable?: boolean;
    multiple?: boolean;
    error?: string | boolean;
    disabled?: boolean;
    readonly?: boolean;
    centered?: boolean;
    readonlyTop?: boolean;
    clearable?: boolean;
    required?: boolean;
    size?: `${SelectSize}`;
    textKey?: string;
    valueKey?: string;
    returnObject?: boolean;
    bg?: boolean;
    mobile?: boolean;
  }

  interface SelectEmits {
    (e: 'update:modelValue', value: ModelValueType): void;
    (e: 'filter', value: string): void;
    (e: 'focus'): void;
    (e: 'blur'): void;
    (e: 'clear'): void;
  }

  const props = withDefaults(defineProps<SelectProps>(), {
    size: SelectSize.S,
    clearable: false,
    disabled: false,
    readonly: false,
    centered: false,
    readonlyTop: false,
    required: false,
    label: '',
    placeholder: '',
    note: '',
    filterable: false,
    multiple: false,
    error: '',
    textKey: 'label',
    valueKey: 'value',
    returnObject: false,
    bg: true,
    mobile: false,
  });

  const emit = defineEmits<SelectEmits>();
  const slots = useSlots();

  const filterQuery = ref('');
  const isFocused = ref(false);
  const visibleMenu = ref(false);
  const menuRef = ref<typeof DropdownMenu | null>(null);
  const selectRef = ref<HTMLElement | null>(null);
  const fieldRef = ref<typeof Field | null>(null);
  const searchRef = ref<HTMLInputElement | null>(null);
  const customSelectRef = ref<HTMLElement | null>(null);
  const mobileSelectedValue = ref<string | number>('');
  const { width: selectRefWidth } = useElementSize(selectRef);

  const clearButtonSize = computed(() => CLEAR_BUTTON_SIZE[props.size] || IconSize.M);
  const menuSize = computed(() => MENU_SIZE[props.size]);
  const iconSize = computed(() => CHEVRON_ICON_SIZE[props.size] || IconSize.M);
  const labelSize = computed(() => LABEL_SIZE[props.size]);
  const selectTextVariant = computed(() => {
    if (props.readonly) {
      return SELECT_VARIANT_BY_SIZE_READONLY[props.size] || TypoVariant.BodyMRegular;
    }

    return SELECT_VARIANT_BY_SIZE[props.size] || TypoVariant.BodyMRegular;
  });
  const infoTextVariant = computed(() => INFO_VARIANT_BY_SIZE[props.size] || TypoVariant.BodyMRegular);
  const leftElementsIsVisible = computed(() => !props.label || isFocused.value || !!innerValue.value);
  const isOptionsAreObjects = computed(
    () => props.options && typeof props.options[0] === 'object' && props?.options[0] !== null,
  );

  const innerValue = computed({
    get() {
      return props.modelValue;
    },
    set(value: ModelValueType) {
      emit('update:modelValue', value);
    },
  });

  const selectedOption = computed(() => {
    if (isOptionsAreObjects.value) {
      if (props.returnObject && innerValue.value !== null) {
        return props.options?.find(
          (option) => innerValue.value && option[props.valueKey] === innerValue.value[props.valueKey],
        );
      }
      return props.options?.find((option) => option[props.valueKey] === innerValue.value);
    }

    return innerValue.value;
  });

  const textValue = computed(() => {
    if (isOptionsAreObjects.value) {
      return selectedOption.value?.[props.textKey] ?? props.placeholder;
    }

    return innerValue.value ?? props.placeholder;
  });

  const currentSelectRef = computed(() => (slots.customValue ? customSelectRef.value : selectRef.value));

  const clearButtonIsVisible = computed(
    () => props.clearable && !!innerValue.value && !props.disabled && !props.readonly && !props.readonlyTop,
  );

  const computedOptions = computed(() =>
    props.options
      ? props.options
          .map((option) => {
            if (typeof option !== 'object' || option === null) {
              return {
                label: option,
                value: option,
                selected: selectedOption.value === option,
              };
            }

            return {
              label: option[props.textKey],
              value: option[props.valueKey],
              selected: selectedOption.value?.[props.valueKey] === option[props.valueKey],
            };
          })
          .filter(({ label }) => String(label).trim().toLowerCase().includes(filterQuery.value.trim().toLowerCase()))
      : [],
  );

  const hasSearchResults = computed(() => {
    return filterQuery.value?.length > 0 ? computedOptions.value?.length > 0 : true;
  });

  const prependMenuIsVisible = computed(
    () => props.options.length === 0 || props.filterable || !hasSearchResults.value || slots.prependOption,
  );

  const onFocus = () => {
    if (props.disabled || props.readonly || props.readonlyTop || props.mobile) {
      return;
    }

    fieldRef.value?.focus();
    isFocused.value = true;
    emit('focus');
  };

  const onBlur = (e) => {
    if (props.mobile) {
      return;
    }

    if (menuRef.value?.$el.contains(e.relatedTarget)) {
      return;
    }

    fieldRef.value?.blur();
    visibleMenu.value = false;
    isFocused.value = false;
    emit('blur');
  };

  const onSearchBlur = () => {
    currentSelectRef.value?.focus();
  };

  const onClear = () => {
    innerValue.value = null;
    emit('clear');
  };

  const onFieldClick = (e) => {
    e.preventDefault();

    if (props.disabled || props.readonly || props.readonlyTop) {
      return;
    }

    if (props.mobile) {
      visibleMenu.value = true;
      mobileSelectedValue.value = computedOptions.value.find((option) => option.selected)?.value ?? '';
      return;
    }

    visibleMenu.value = !visibleMenu.value;

    if (visibleMenu.value && !isFocused.value) {
      currentSelectRef.value?.focus();
    } else if (!visibleMenu.value && isFocused.value) {
      currentSelectRef.value?.blur();
    }
  };

  const onClickOption = (optionValue: string | number) => {
    const option = isOptionsAreObjects.value
      ? props.options?.find((el) => el[props.valueKey] === optionValue)
      : optionValue;

    innerValue.value = option && typeof option === 'object' && !props.returnObject ? option[props.valueKey] : option;

    filterQuery.value = '';
    fieldRef.value?.blur();
    visibleMenu.value = false;
    isFocused.value = false;
    emit('blur');
  };

  const onConfirmMobileSelection = () => {
    if (mobileSelectedValue.value != null && mobileSelectedValue.value !== '') {
      onClickOption(mobileSelectedValue.value);
    }
  };
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    :class="[
      'tl-select',
      `tl-select_size-${size}`,
      {
        'tl-select_focused': isFocused,
        'tl-select_disabled': disabled,
        'tl-select_readonly': readonly,
        'tl-select_centered': centered,
        'tl-select_readonly-top': readonlyTop,
        'tl-select_unlabeled': !label,
        'tl-select_error': error,
        'tl-select_bg': bg,
      },
    ]"
  >
    <Field
      ref="fieldRef"
      class="tl-select__field"
      :label="label"
      with-dropdown
      :note="note"
      :size="labelSize"
      :filled="!!modelValue"
      :disabled="disabled"
      :readonly="readonly"
      :readonly-filled="readonlyTop"
      :error="error"
      :required="required"
      :label-max-width="selectRefWidth"
      data-test-id="field"
      @click="onFieldClick"
    >
      <div class="tl-select__input-wrapper" data-test-id="input-wrapper">
        <Transition name="fade">
          <div v-if="leftElementsIsVisible && $slots.prepend" class="tl-select__prepend">
            <slot name="prepend" />
          </div>
        </Transition>

        <div
          v-if="!$slots.customValue"
          ref="selectRef"
          tabindex="0"
          aria-label="select"
          :class="['tl-select__source', { 'tl-select__source_placeholder': !innerValue && placeholder }]"
          data-select-source
          data-test-id="select"
          @focus="onFocus"
          @blur="onBlur"
          @keydown.enter="onFieldClick"
        >
          <Typo class="tl-select__source-typo" :variant="selectTextVariant">
            {{ textValue }}
          </Typo>
        </div>

        <div
          v-else
          ref="customSelectRef"
          tabindex="0"
          class="tl-select__source-custom"
          data-select-source
          @keydown.enter="onFieldClick"
          @focus="onFocus"
          @blur="onBlur"
        >
          <slot v-if="selectedOption" name="customValue" :slot-props="$props" :selected-option="selectedOption" />
        </div>

        <div class="tl-select__right-group">
          <Transition name="fade">
            <Button
              v-if="clearButtonIsVisible"
              appearance="tertiary"
              type="icon"
              icon="close"
              :size="clearButtonSize"
              data-test-id="clear-button"
              class="tl-select__clear"
              @click.prevent.stop="onClear"
            />
          </Transition>

          <div class="tl-select__append">
            <slot name="append" />
          </div>

          <Icon
            v-if="!readonly && !readonlyTop"
            name="chevron-down"
            :size="iconSize"
            data-test-id="icon-left"
            :class="['tl-select__icon', { 'tl-select__icon_active': visibleMenu }]"
            @click.prevent.stop
          />
        </div>
      </div>

      <template v-if="!mobile" #append>
        <DropdownMenu
          ref="menuRef"
          :size="menuSize"
          full-width
          trigger="manual"
          :visible="visibleMenu"
          :options="computedOptions"
          @option-click="onClickOption"
        >
          <template #prepend>
            <div v-if="prependMenuIsVisible" class="tl-select__menu-prepend" tabindex="-1">
              <Input
                v-if="filterable && options.length > 0"
                ref="searchRef"
                v-model="filterQuery"
                :size="size"
                placeholder="Поиск"
                data-test-id="filter-input"
                @blur="onSearchBlur"
                @update:modelValue="$emit('filter', $event)"
              >
                <template #prepend>
                  <Icon class="tl-select__icon" name="search" :size="iconSize" />
                </template>
              </Input>

              <Typo
                v-if="!hasSearchResults"
                class="tl-select__message tl-select__no-result"
                :variant="infoTextVariant"
                data-test-id="no-result-filter"
              >
                Нет совпадений
              </Typo>

              <Typo
                v-else-if="options.length === 0"
                class="tl-select__message tl-select__empty"
                :variant="infoTextVariant"
                data-test-id="no-result-filter"
              >
                Список пуст
              </Typo>
              <slot name="prependOption" />
            </div>
          </template>

          <template #option="{ option }">
            <slot name="option" :option="options?.find((el) => el[valueKey] === option.value)" />
          </template>

          <template #append>
            <div v-if="$slots.appendOption" class="tl-select__menu-append" tabindex="-1">
              <slot name="appendOption" />
            </div>
          </template>
        </DropdownMenu>
      </template>
    </Field>

    <BottomSheet v-if="mobile && visibleMenu" @close="visibleMenu = false">
      <template #header>
        <div class="tl-select__header">
          <Button
            appearance="tertiary"
            type="icon"
            icon="close"
            size="m"
            class="tl-select__close"
            @click="visibleMenu = false"
          />
          <Typo variant="h-5">{{ label }}</Typo>
        </div>
      </template>

      <div class="tl-select__content">
        <div v-if="filterable && options.length > 0" class="tl-select__search-wrapper">
          <Input
            v-model="filterQuery"
            size="l"
            placeholder="Поиск"
            data-test-id="filter-input"
            class="tl-select__search"
            @update:modelValue="$emit('filter', $event)"
          >
            <template #prepend>
              <Icon class="tl-select__icon" name="search" size="l" />
            </template>
          </Input>
        </div>

        <Typo
          v-if="!hasSearchResults"
          class="tl-select__message tl-select__no-result"
          :variant="TypoVariant.BodyLRegular"
        >
          Нет совпадений
        </Typo>

        <Typo
          v-else-if="options.length === 0"
          :variant="TypoVariant.BodyLRegular"
          class="tl-select__message tl-select__empty"
        >
          Список пуст
        </Typo>

        <div v-else class="tl-select__options">
          <Radio
            v-for="option in computedOptions"
            :key="option.value"
            v-model="mobileSelectedValue"
            :value="option.value"
            :label="option.label"
            name="tl-select-mobile"
            size="l"
            class="tl-select__option"
          />
        </div>
      </div>

      <template #footer>
        <Button appearance="primary" size="l" class="tl-select__confirm" @click="onConfirmMobileSelection">
          Выбрать
        </Button>
      </template>
    </BottomSheet>
  </div>
</template>

<style lang="scss" scoped>
  .tl-select {
    $root: &;

    width: 100%;

    &_bg {
      &:not(#{$root}_disabled):not(#{$root}_readonly-top) {
        #{$root}__input-wrapper {
          background-color: var(--color-bg-primary);
        }
      }
    }

    &_centered {
      #{$root}__source {
        justify-content: center;
      }
    }

    &_focused,
    &_unlabeled {
      #{$root}__source {
        &_placeholder {
          opacity: 1;
        }
      }
    }

    &_disabled {
      // @todo на вынесение в Field
      #{$root}__source {
        color: var(--color-text-tertiary);

        &_placeholder {
          color: var(--color-text-tertiary);
        }
      }

      #{$root}__icon {
        fill: var(--color-text-tertiary);
      }
    }

    &_readonly-top {
      #{$root}__source {
        color: var(--color-text-primary);

        &_placeholder {
          color: var(--color-text-primary);
        }
      }
    }

    &__field {
      width: 100%;
    }

    &__input-wrapper {
      align-items: center;
      cursor: pointer;
      display: flex;
      height: 100%;
      transition: var(--transition-default);
    }

    &__source,
    &__source-custom {
      // @todo на вынос в Field
      align-items: center;
      background: transparent;
      border: none;
      color: var(--color-text-primary);
      display: flex;
      margin: 0;
      min-width: 10px;
      outline: none;
      padding: 0;
      width: 100%;

      &_placeholder {
        color: var(--color-text-secondary);
        opacity: 0;
        transition: var(--transition-default);
      }
    }

    &__source-typo {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__right-group {
      align-items: center;
      display: flex;
      margin-left: auto;
    }

    &__icon_active {
      transform: rotate(180deg);
    }

    &__icon {
      fill: var(--color-neutral-50);
      transition: var(--transition-default);
    }

    &__menu-prepend,
    &__menu-append {
      padding: var(--spacing-m) var(--spacing-m) 0 var(--spacing-m);
    }

    &__no-result,
    &__empty {
      color: var(--color-text-secondary);
      padding: var(--spacing-xs) 0;
    }

    &__empty {
      padding: var(--spacing-xs) 0;
    }

    &__no-result {
      margin-top: var(--spacing-xs);
    }

    &__search {
      margin-bottom: var(--spacing-xl);
    }

    &__header {
      align-items: center;
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    &__close {
      align-self: flex-end;
    }

    &__options {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
    }

    &__confirm {
      width: 100%;
    }

    &__prepend,
    &__append {
      align-items: center;
      display: flex;
      gap: var(--spacing-xs);
      text-wrap: nowrap;
    }

    &__prepend {
      margin-right: var(--spacing-xs);
    }

    &_size {
      &-s {
        #{$root}__menu-prepend {
          padding: var(--spacing-m) var(--spacing-s) 0 var(--spacing-s);
        }

        #{$root}__input-wrapper {
          padding: 0 var(--spacing-xs);
        }

        #{$root}__source,
        #{$root}__source-custom {
          height: 30px;
        }
      }

      &-m {
        #{$root}__input-wrapper {
          padding: 0 var(--spacing-s);
        }

        #{$root}__source,
        #{$root}__source-custom {
          height: 38px;
        }
      }

      &-l {
        #{$root}__input-wrapper {
          padding: 0 var(--spacing-m);
        }

        #{$root}__source,
        #{$root}__source-custom {
          height: 46px;
        }
      }

      &-xl {
        #{$root}__input-wrapper {
          padding: 0 var(--spacing-m);
        }

        #{$root}__source,
        #{$root}__source-custom {
          height: 54px;
        }
      }
    }
  }
</style>
